import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Pagination
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { getCompanies, createCompany, updateCompany } from '../services/api';
import CompanyModal from '../components/modals/CompanyModal';
import PageHeader from '../components/layout/PageHeader';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 25;

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data || []);
    } catch (error) {
      showAlert('Error fetching companies', 'error');
      setCompanies([]); // Set empty array on error
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const handleCreateCompany = () => {
    setSelectedCompany(null);
    setModalOpen(true);
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setModalOpen(true);
  };

  const handleModalSubmit = async (formData) => {
    try {
      if (selectedCompany) {
        await updateCompany(selectedCompany.id, formData);
        showAlert('Company updated successfully');
      } else {
        await createCompany(formData);
        showAlert('Company created successfully');
      }
      setModalOpen(false);
      fetchCompanies();
    } catch (error) {
      showAlert(error.response?.data?.message || 'Error processing request', 'error');
    }
  };

  // Calculate pagination
  const paginatedCompanies = useMemo(() => {
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    return companies.slice(indexOfFirstRecord, indexOfLastRecord);
  }, [companies, currentPage]);

  const totalPages = Math.ceil(companies.length / recordsPerPage);

  return (
    <Box>
      <PageHeader title="Companies">
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateCompany}
        >
          New Company
        </Button>
      </PageHeader>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No companies found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedCompanies.map((company) => (
              <TableRow 
                key={company.id}
                hover
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{company.name}</TableCell>
                <TableCell>{company.email}</TableCell>
                <TableCell>{company.phone}</TableCell>
                <TableCell>{company.address}</TableCell>
                <TableCell>
                  <Chip
                    label={company.active ? 'Active' : 'Inactive'}
                    color={company.active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleEditCompany(company)}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            )))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {companies.length > recordsPerPage && (
        <Box display="flex" justifyContent="center" mt={1.5}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, value) => setCurrentPage(value)}
            color="primary"
          />
        </Box>
      )}

      <CompanyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
        initialData={selectedCompany}
      />

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Companies;
